import React, { useCallback, useEffect, useState } from "react";
import config from "../config";

function StatCard({ label, value, sub, accent = "#2563eb" }) {
  return (
    <div
      style={{
        background: "#f8fafc",
        border: "1px solid #e2e8f0",
        borderRadius: 10,
        padding: "16px 18px",
        minWidth: 140,
        flex: "1 1 140px",
      }}
    >
      <div style={{ fontSize: 13, color: "#64748b", marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700, color: accent, lineHeight: 1.1 }}>{value}</div>
      {sub ? <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 6 }}>{sub}</div> : null}
    </div>
  );
}

export default function AdminOverview({ adminToken }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchOverview = useCallback(async () => {
    if (!adminToken) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${config.API_URL}/admin/overview`, {
        headers: { token: adminToken, "Content-Type": "application/json" },
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `HTTP ${res.status}`);
      }
      setData(await res.json());
    } catch (err) {
      setError(err.message || "Could not load overview");
    } finally {
      setLoading(false);
    }
  }, [adminToken]);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  if (!adminToken) return null;

  return (
    <div
      style={{
        maxWidth: 960,
        margin: "0 auto 28px",
        padding: 24,
        background: "#fff",
        borderRadius: 12,
        boxShadow: "0 2px 12px rgba(30,58,92,0.07)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          marginBottom: 20,
          flexWrap: "wrap",
        }}
      >
        <h2 style={{ margin: 0, color: "#1e3a8a", fontSize: 22 }}>Platform overview</h2>
        <button
          type="button"
          className="ffb-btn"
          onClick={fetchOverview}
          disabled={loading}
          style={{ background: "#64748b", fontSize: 14 }}
        >
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {error && (
        <div style={{ color: "#dc2626", marginBottom: 16, fontSize: 14 }}>{error}</div>
      )}

      {data && (
        <>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
            <StatCard label="Total users" value={data.users.total} sub={`${data.users.verified} verified · ${data.users.unverified} pending`} accent="#2563eb" />
            <StatCard label="Total leagues" value={data.leagues.total} sub={`${data.leagues.public} public · ${data.leagues.private} private`} accent="#059669" />
            <StatCard
              label="Joinable public leagues"
              value={data.leagues.public_joinable}
              sub={
                data.public_registration_open
                  ? `${data.leagues.total_open_spots_public} open spots (Quick Join)`
                  : "Registration closed for season"
              }
              accent="#7c3aed"
            />
            <StatCard
              label="Drafts complete"
              value={data.leagues.draft_complete}
              sub={Object.entries(data.drafts || {}).map(([k, v]) => `${k}: ${v}`).join(" · ") || "No draft sessions"}
              accent="#0ea5e9"
            />
          </div>

          {data.public_joinable_leagues?.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <h3 style={{ margin: "0 0 10px", fontSize: 15, color: "#334155" }}>
                Public leagues open for Quick Join
              </h3>
              <ul style={{ margin: 0, paddingLeft: 20, color: "#475569", fontSize: 14, lineHeight: 1.6 }}>
                {data.public_joinable_leagues.map((lg) => (
                  <li key={lg.id}>
                    <strong>{lg.name}</strong> (ID {lg.id}) — {lg.members}/{lg.team_count} filled,{" "}
                    {lg.open_spots} open · {lg.draft_type}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div style={{ fontSize: 12, color: "#94a3b8" }}>
            Also: {data.leagues.private_with_open_spots} private league(s) with open roster slots ·{" "}
            {data.leagues.public_with_open_spots} public with any open spots
            {data.generated_at ? ` · Updated ${new Date(data.generated_at).toLocaleString()}` : ""}
          </div>
        </>
      )}

      {!data && !error && loading && (
        <div style={{ color: "#64748b", fontSize: 14 }}>Loading overview…</div>
      )}
    </div>
  );
}
